import React, { useEffect, useState } from 'react';
import * as api from '../lib/api';
import { apiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { LoadingTable, ErrorState, EmptyState } from './States';
import { DataTable } from './DataTable';
import { ConfirmDialog } from './ConfirmDialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Plus, Pencil, KeyRound, Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const ROLE = { admin: 'Direzione', control: 'Controllo di Gestione' };

export default function UsersPanel() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [error, setError] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(false);

  const [cf, setCf] = useState({ name: '', email: '', role: 'control', password: '' });
  const [ef, setEf] = useState({ name: '', role: 'control', disabled: false });
  const [newPw, setNewPw] = useState('');

  const load = () => { setError(false); api.usersList().then(setList).catch(() => setError(true)); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try { await api.userCreate({ ...cf, email: cf.email.trim().toLowerCase() }); toast.success('Utente creato'); setCreateOpen(false); setCf({ name: '', email: '', role: 'control', password: '' }); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const openEdit = (u) => { setEf({ name: u.name || '', role: u.role, disabled: !!u.disabled }); setEditing(u); };
  const saveEdit = async () => {
    setBusy(true);
    try { await api.userUpdate(editing.email, ef); toast.success('Utente aggiornato'); setEditing(null); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const doReset = async () => {
    setBusy(true);
    try { await api.userResetPassword(resetting.email, newPw); toast.success('Password reimpostata'); setResetting(null); setNewPw(''); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const doDelete = async () => {
    setBusy(true);
    try { await api.userDelete(confirmDel.email); toast.success('Utente eliminato'); setConfirmDel(null); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (error) return <ErrorState onRetry={load} />;
  if (!list) return <LoadingTable />;

  const columns = [
    { key: 'name', header: 'Nome', render: (u) => <span className="font-medium">{u.name || '—'}{u.email === user?.email && <Badge variant="secondary" className="ml-2 text-[10px]">tu</Badge>}</span> },
    { key: 'email', header: 'Email', render: (u) => <span className="text-muted-foreground text-xs">{u.email}</span> },
    { key: 'role', header: 'Ruolo', render: (u) => <Badge variant="outline" className={u.role === 'admin' ? 'bg-accent text-accent-foreground' : ''}>{ROLE[u.role] || u.role}</Badge>, sortValue: (u) => u.role },
    { key: 'disabled', header: 'Stato', render: (u) => u.disabled ? <Badge variant="outline" className="bg-[hsl(var(--danger))]/12 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30">Disabilitato</Badge> : <Badge className="bg-[hsl(var(--success))] text-white">Attivo</Badge>, sortValue: (u) => (u.disabled ? 1 : 0) },
    { key: 'actions', header: 'Azioni', sortable: false, align: 'right', render: (u) => (
      <div className="flex items-center justify-end gap-1">
        <Button variant="ghost" size="icon" className="h-8 w-8" title="Modifica" onClick={() => openEdit(u)} data-testid="user-edit-button"><Pencil className="h-4 w-4" /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" title="Reimposta password" onClick={() => { setNewPw(''); setResetting(u); }} data-testid="user-reset-button"><KeyRound className="h-4 w-4" /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" title="Elimina" onClick={() => setConfirmDel(u)} data-testid="user-delete-button"><Trash2 className="h-4 w-4" /></Button>
      </div>
    ) },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div><h3 className="text-sm font-semibold">Utenti</h3><p className="text-xs text-muted-foreground mt-0.5">{list.length} utenti · gestione ruoli e accessi (solo Direzione)</p></div>
        <Button onClick={() => setCreateOpen(true)} data-testid="user-create-button"><UserPlus className="h-4 w-4 mr-1.5" />Nuovo utente</Button>
      </div>
      {list.length === 0 ? <EmptyState title="Nessun utente" /> : <DataTable columns={columns} rows={list} getRowId={(u) => u.email} />}

      {/* Create */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="form-modal">
          <DialogHeader><DialogTitle>Nuovo utente</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5"><Label>Nome</Label><Input value={cf.name} onChange={(e) => setCf({ ...cf, name: e.target.value })} data-testid="user-field-name" /></div>
            <div className="grid gap-1.5"><Label>Email</Label><Input type="email" value={cf.email} onChange={(e) => setCf({ ...cf, email: e.target.value })} placeholder="nome@fier.holding" data-testid="user-field-email" /></div>
            <div className="grid gap-1.5"><Label>Ruolo</Label>
              <Select value={cf.role} onValueChange={(v) => setCf({ ...cf, role: v })}><SelectTrigger data-testid="user-field-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="admin">Direzione (accesso completo)</SelectItem><SelectItem value="control">Controllo di Gestione</SelectItem></SelectContent></Select>
            </div>
            <div className="grid gap-1.5"><Label>Password</Label><Input type="password" value={cf.password} onChange={(e) => setCf({ ...cf, password: e.target.value })} data-testid="user-field-password" /><span className="text-[11px] text-muted-foreground">Almeno 8 caratteri.</span></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button><Button onClick={create} disabled={busy} data-testid="user-create-confirm">{busy ? 'Creazione…' : 'Crea utente'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="form-modal">
          <DialogHeader><DialogTitle>Modifica utente</DialogTitle></DialogHeader>
          {editing && <div className="grid gap-4 py-2">
            <div className="text-xs text-muted-foreground">{editing.email}</div>
            <div className="grid gap-1.5"><Label>Nome</Label><Input value={ef.name} onChange={(e) => setEf({ ...ef, name: e.target.value })} data-testid="user-edit-name" /></div>
            <div className="grid gap-1.5"><Label>Ruolo</Label>
              <Select value={ef.role} onValueChange={(v) => setEf({ ...ef, role: v })}><SelectTrigger data-testid="user-edit-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="admin">Direzione (accesso completo)</SelectItem><SelectItem value="control">Controllo di Gestione</SelectItem></SelectContent></Select>
            </div>
            <div className="flex items-center gap-2"><Checkbox id="dis" checked={ef.disabled} onCheckedChange={(c) => setEf({ ...ef, disabled: !!c })} data-testid="user-edit-disabled" /><Label htmlFor="dis" className="font-normal">Account disabilitato (blocca l'accesso)</Label></div>
          </div>}
          <DialogFooter><Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button><Button onClick={saveEdit} disabled={busy} data-testid="user-edit-confirm">{busy ? 'Salvataggio…' : 'Salva'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!resetting} onOpenChange={(o) => !o && setResetting(null)}>
        <DialogContent data-testid="form-modal">
          <DialogHeader><DialogTitle>Reimposta password</DialogTitle></DialogHeader>
          {resetting && <div className="grid gap-1.5 py-2"><Label>Nuova password per {resetting.email}</Label><Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="user-reset-input" /><span className="text-[11px] text-muted-foreground">Almeno 8 caratteri.</span></div>}
          <DialogFooter><Button variant="outline" onClick={() => setResetting(null)}>Annulla</Button><Button onClick={doReset} disabled={busy} data-testid="user-reset-confirm">{busy ? 'Attendere…' : 'Reimposta'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)} title="Eliminare l'utente?" description={confirmDel ? `${confirmDel.name || confirmDel.email} verrà eliminato definitivamente.` : ''} onConfirm={doDelete} loading={busy} />
    </div>
  );
}
